// Centralized company configuration for portal branding
export interface CompanyConfig {
	name: string;
	portalTitle: string;
	tagline: string;
	subtitle: string;
	colors: {
		primary: string;
		secondary: string;
		accent: string;
		bgGradient: string;
	};
}

// Default fallback configuration
const defaultConfig: CompanyConfig = {
	name: "TrustMedis Healthcare",
	portalTitle: "TrustMedis Portal",
	tagline: "",
	subtitle: "Pilih lokasi terdekat untuk melakukan booking online",
	colors: {
		primary: "#1f2937", // Neutral gray-900
		secondary: "#3b82f6", // Professional blue
		accent: "#10b981", // Success green
		bgGradient:
			"linear-gradient(135deg, #1f2937 0%, #374151 50%, #3b82f6 100%)",
	},
};

// Cache for company settings
let cachedCompanySettings: CompanyConfig | null = null;

// Fetch company settings from API
export async function fetchCompanySettings(
	tenantId?: string,
): Promise<CompanyConfig> {
	try {
		const headers: Record<string, string> = {
			"Content-Type": "application/json",
		};

		// Add tenant ID if provided
		if (tenantId) {
			headers["x-tenant-id"] = tenantId;
		}

		const response = await fetch("/api/company/settings", {
			headers,
		});

		if (!response.ok) {
			console.warn("Failed to fetch company settings, using defaults");
			return defaultConfig;
		}

		const result = await response.json();

		// Map API response to our config interface
		const apiConfig = result.data || result.results || result;

		const companyConfig: CompanyConfig = {
			name: apiConfig.company_name || apiConfig.name || defaultConfig.name,
			portalTitle:
				apiConfig.portal_title ||
				apiConfig.portalTitle ||
				defaultConfig.portalTitle,
			tagline: apiConfig.tagline || defaultConfig.tagline,
			subtitle: apiConfig.subtitle || defaultConfig.subtitle,
			colors: {
				primary: apiConfig.primary_color || defaultConfig.colors.primary,
				secondary: apiConfig.secondary_color || defaultConfig.colors.secondary,
				accent: apiConfig.accent_color || defaultConfig.colors.accent,
				bgGradient: apiConfig.bg_gradient || defaultConfig.colors.bgGradient,
			},
		};

		// Cache the settings
		cachedCompanySettings = companyConfig;
		return companyConfig;
	} catch (error) {
		console.error("Error fetching company settings:", error);
		return defaultConfig;
	}
}

// Get company configuration (sync version with fallback)
export function getCompanyConfig(): CompanyConfig {
	// Return cached settings if available, otherwise return defaults
	return cachedCompanySettings || defaultConfig;
}

// For tenant-specific company config
export function getCompanyConfigForTenant(tenantId?: string): CompanyConfig {
	// For now, return cached or default config
	// In the future, this could have tenant-specific logic
	return getCompanyConfig();
}
